import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import isEmpty from 'lodash.isempty';
import isUndefined from 'lodash.isundefined';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import Alert from '../../../components/Alert';
import { useUserStore } from '../../../components/userStore';
import ServiceCard from './ServiceCard';
import WaitListModal from './WaitListModal';
import { useApplicationStore } from './applicationStore';

const AvailableOrganizations = () => {
	const { user } = useUserStore();
	const {
		zipCode,
		taxonomyId,
		taxonomyDetail,
		setServiceID,
		setProgram,
		ccaOnly,
		referralAgency,
		setTaxonomyDetail,
		setTaxonomyID,
		getClient,
	} = useApplicationStore();
	const navigate = useNavigate();
	const [showWaitListModal, setShowWaitListModal] = useState(false);
	const programsQuery = useQuery({
		queryKey: ['programs', user.coord_agency_id, zipCode, taxonomyId, ccaOnly],
		queryFn: async () => {
			const params = {
				zipCode: zipCode.trim(),
				ccaOnly,
			};
			if (taxonomyId) {
				params.taxonomy = encodeURIComponent(taxonomyId);
			}
			const { data } = await axios.get(
				`/coord_agencies/${user.coord_agency_id}/getServicesByTaxonomyAndPostalCode`,
				{
					params,
				},
			);
			return data;
		},
		enabled:
			!isEmpty(zipCode) && !isEmpty(taxonomyId) && isEmpty(referralAgency),
	});
	const orgProgramsQuery = useQuery({
		queryKey: ['ccaPrograms', user.coord_agency_id, referralAgency?.id],
		queryFn: async () => {
			const { data } = await axios.get(
				`/coord_agencies/${user.coord_agency_id}/cca-org-services`,
				{
					params: { orgId: referralAgency?.id },
				},
			);
			return data;
		},
		enabled:
			!isEmpty(referralAgency) && isEmpty(zipCode) && isEmpty(taxonomyId),
	});

	let nextStepPath = '/newApplication/confirm';
	if (
		(isEmpty(zipCode) || isEmpty(taxonomyId)) &&
		isUndefined(referralAgency)
	) {
		return <Navigate to="/newApplication" replace />;
	}
	if (isEmpty(getClient().name_first) && isEmpty(getClient().name_last)) {
		nextStepPath = '/newApplication/profile';
	}

	const handleOrgChosen = (event, program) => {
		setProgram(program);
		setServiceID(program?.ccAttributes?.service_id);
		if (orgProgramsQuery.isFetched) {
			setTaxonomyDetail(event.target.dataset.taxonomyDetail);
			setTaxonomyID(event.target.dataset.taxonomyId);
		}
		if (program?.ccAttributes?.waitlist_ind) {
			event.preventDefault();
			setShowWaitListModal(true);
			return;
		}
		navigate(nextStepPath);
	};

	const handleAddToWaitList = () => {
		setShowWaitListModal(false);
		navigate(nextStepPath);
	};

	const isLoadingPrograms =
		programsQuery?.isFetching || orgProgramsQuery?.isFetching;

	let programsData = [];
	if (programsQuery.isFetched) {
		programsData = programsQuery.data;
	} else if (orgProgramsQuery.isFetched) {
		programsData = orgProgramsQuery.data;
	}

	return (
		<>
			<h1>
				{zipCode ? (
					<>
						{taxonomyDetail} - Organizations serving {zipCode}
					</>
				) : (
					<>Services provided by {referralAgency.agency_name}</>
				)}
			</h1>

			<div className="available-organizations">
				{zipCode && (
					<div className="mb-2">
						<CheckCircleIcon className="inline-block w-4 h-4 mr-1 text-success" />
						<em className="text-sm">Tandem Advanced Application</em>
					</div>
				)}
				{isLoadingPrograms && (
					<div className="flex flex-col h-32">
						<span className="self-center flex-1 block loading loading-spinner loading-lg text-primary"></span>
					</div>
				)}
				{programsQuery.isError && (
					<div className="mt-6">
						<Alert message="There was a problem finding available organizations" />
					</div>
				)}
				{orgProgramsQuery.isError && (
					<div className="mt-6">
						<Alert message="There was a problem finding services" />
					</div>
				)}
				{programsQuery.isFetched && programsQuery?.data?.length === 0 && (
					<div className="mt-4">
						There are no Organizations in {zipCode} that offer the selected
						Program
					</div>
				)}
				{orgProgramsQuery.isFetched && orgProgramsQuery?.data?.length === 0 && (
					<div className="mt-4">
						There are no services available for the selected organization
					</div>
				)}
				{Array.isArray(programsData) &&
					programsData.map((program) => {
						let taxonomyId, taxonomyDetail;
						const taxonomy = program.service_taxonomy.find(
							({ taxonomy_id }) =>
								taxonomy_id === program?.ccAttributes?.taxonomy_id,
						);
						if (taxonomy) {
							({ taxonomy_id: taxonomyId, taxonomy_detail: taxonomyDetail } =
								taxonomy);
						}
						return (
							<ServiceCard
								key={`${program.id}-${taxonomyId}`}
								taxonomyId={taxonomyId}
								taxonomyDetail={taxonomyDetail}
								program={program}
								zipCode={zipCode}
								onOrgChosen={handleOrgChosen}
							/>
						);
					})}
			</div>
			<WaitListModal
				isOpen={showWaitListModal}
				onContinue={() => handleAddToWaitList()}
				onCancel={() => setShowWaitListModal(false)}
			/>
		</>
	);
};

export default AvailableOrganizations;
